// Product page behaviour; markup and the pd-data JSON come from sections/stally-rd-product*.liquid.
const cartUrl = path => `${window.Shopify?.routes?.root || '/'}${path}`;
const escapeHTML = value => String(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
// Shopify's image CDN resizes by its width parameter
const resized = (src, width) => {
  try {
    const url = new URL(src, location.href);
    url.searchParams.set('width', width);
    return url.href;
  } catch {
    return src;
  }
};
// The large photo, and the rail beside it (a row of four under it on phones); see the gallery comment in the section.
const RAIL_LENGTH = 4;
const MAIN_SIZES = '(min-width: 1500px) 640px, (min-width: 993px) 42vw, (min-width: 651px) 70vw, 100vw';
const RAIL_SIZES = '(min-width: 1500px) 170px, (min-width: 993px) 11vw, (min-width: 651px) 18vw, 25vw';
const LIGHTBOX_WIDTHS = [900, 1400, 2000];
const motion = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
// the current product section's gallery opener (initProduct sets it), for the Details matter and Stallys in the Wild
// photos below
let openGallery = null;

function initProduct(root = document) {
  const get = id => root.querySelector(`#${id}`);
  const data = JSON.parse(get('pd-data')?.textContent || 'null');
  const form = get('pd-form');
  if (!data || !form) return;
  const { colorIndex, sizeIndex, variants } = data;
  let color = data.color;
  let size = data.size;
  let busy = false;
  // the "One size" button of a single-variant product is display only: always chosen, never redrawn
  const sizeButtons = [...root.querySelectorAll('#pd-sizes .size-choice:not([data-one-size])')];
  const swatchButtons = [...root.querySelectorAll('#pd-swatches button.swatch')];
  const gallery = get('pd-gallery');
  const figures = [...gallery.querySelectorAll('figure')];
  const add = get('pd-add'), notify = get('pd-notify'), status = get('pd-status'), variantInput = get('pd-variant-id');
  const preorder = get('pd-preorder');
  const finalSale = get('pd-final-sale');
  const colorMatches = v => colorIndex === null || v.options[colorIndex] === color;
  const sizeVariant = name => variants.find(v => colorMatches(v) && v.options[sizeIndex] === name);
  const selected = () => variants.find(v => colorMatches(v) && (sizeIndex === null || v.options[sizeIndex] === size));
  if (sizeIndex !== null && sizeButtons.length === 1) size = sizeButtons[0].dataset.size;

  // Figures are re-parented rather than hidden so the CSS :only-child rule still sees a lone photo.
  function drawGallery() {
    const matching = colorIndex === null ? figures : [...figures.filter(f => f.dataset.color === color), ...figures.filter(f => !f.dataset.color)];
    const shown = (matching.length ? matching : figures).slice(0, data.photosPerColor);
    shown.forEach(f => { f.hidden = false; });
    gallery.replaceChildren(...shown);
    const firstImage = gallery.querySelector('img');
    if (firstImage) { firstImage.loading = 'eager'; firstImage.fetchPriority = 'high'; }
    drawRail();
  }

  // With more photos than slots, the fourth rail photo shows "+N more", N counting itself.
  function drawRail() {
    const shown = [...gallery.querySelectorAll('figure:not([hidden])')];
    shown.forEach((figure, index) => {
      const slot = index === 0 ? 'main' : index <= RAIL_LENGTH ? 'rail' : 'rest';
      figure.dataset.slot = slot;
      const image = figure.querySelector('img');
      if (image) image.sizes = slot === 'main' ? MAIN_SIZES : RAIL_SIZES;
      const more = index === RAIL_LENGTH && shown.length > RAIL_LENGTH + 1 ? shown.length - RAIL_LENGTH : 0;
      figure.toggleAttribute('data-more', more > 0);
      const label = figure.querySelector('.pd-more');
      if (label) label.innerHTML = more ? `<b>+${more}</b> more` : '';
      figure.querySelector('.pd-open')?.setAttribute('aria-label', `${image?.alt || 'Photo'}, photo ${index + 1} of ${shown.length}${more ? `, ${more} more` : ''}`);
    });
  }

  // Lightbox slides are built on open, so they follow the chosen color (or the photo set opened from).
  const lightbox = get('pd-lightbox');
  const track = get('pd-lb-track');
  const thumbs = get('pd-lb-thumbs');
  const counter = get('pd-lb-count');
  // A smooth goTo() passes the slides in between, so while it runs (`landing`) scroll events don't move the counter.
  // The slide the track rests on at scrollend, or after SETTLE_MS with no scroll event (no scrollend), is current.
  const SETTLE_MS = 160;
  let current = -1;
  let landing = null;
  let settleTimer = 0;
  const pad = number => String(number).padStart(2, '0');
  const slideAt = () => (track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
  function setCurrent(index) {
    const total = track.children.length;
    const next = Math.max(0, Math.min(index, total - 1));
    const changed = next !== current;
    current = next;
    // written only when it changes, so the polite live region announces each photo once
    const text = `${pad(current + 1)} / ${pad(total)}`;
    if (counter.textContent !== text) counter.textContent = text;
    if (!changed) return;
    thumbs.querySelectorAll('.pd-lb-thumb').forEach((thumb, position) => {
      if (position === current) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    // aria-disabled, not disabled: a disabled button would drop keyboard focus out of the dialog
    lightbox.querySelector('.pd-lb-prev').setAttribute('aria-disabled', String(current === 0));
    lightbox.querySelector('.pd-lb-next').setAttribute('aria-disabled', String(current === total - 1));
    const thumb = thumbs.children[current];
    if (thumb && thumbs.scrollWidth > thumbs.clientWidth) thumbs.scrollTo({ left: thumb.offsetLeft - (thumbs.clientWidth - thumb.offsetWidth) / 2, behavior: motion() });
  }
  function settle() {
    clearTimeout(settleTimer);
    landing = null;
    // closing hides the track and resets its scroll: the closed gallery keeps the photo it showed
    if (lightbox.open) setCurrent(slideAt());
  }
  function goTo(index, behavior = motion()) {
    const target = Math.max(0, Math.min(index, track.children.length - 1));
    clearTimeout(settleTimer);
    if (behavior === 'instant' || target === slideAt()) {
      // nothing to animate (or no motion wanted): no scroll to wait for
      landing = null;
      track.scrollTo({ left: target * track.clientWidth, behavior: 'instant' });
      setCurrent(target);
      return;
    }
    landing = target;
    track.scrollTo({ left: target * track.clientWidth, behavior });
    setCurrent(target);
    // if the scroll never starts, nothing else would end the wait
    settleTimer = setTimeout(settle, SETTLE_MS * 4);
  }
  function openLightbox(start, photos = [...gallery.querySelectorAll('figure:not([hidden]) img')]) {
    if (!lightbox || !photos.length) return;
    track.innerHTML = photos.map((image, index) => {
      const src = image.currentSrc || image.src;
      return `<figure class="pd-lb-slide" role="group" aria-roledescription="slide" aria-label="${index + 1} of ${photos.length}"><img src="${escapeHTML(resized(src, 1400))}" srcset="${escapeHTML(LIGHTBOX_WIDTHS.map(width => `${resized(src, width)} ${width}w`).join(', '))}" sizes="min(100vw, 80dvh)" alt="${escapeHTML(image.alt)}" width="1200" height="1500"${index === start ? '' : ' loading="lazy"'}></figure>`;
    }).join('');
    thumbs.innerHTML = photos.map((image, index) => `<button type="button" class="pd-lb-thumb" data-index="${index}" aria-label="Show photo ${index + 1}"><img src="${escapeHTML(resized(image.currentSrc || image.src, 160))}" alt="" width="120" height="150" loading="lazy"></button>`).join('');
    // one photo: no prev/next or thumbnails to tab through
    lightbox.toggleAttribute('data-single', photos.length < 2);
    current = -1;
    lightbox.showModal();
    goTo(start, 'instant');
  }
  openGallery = openLightbox;
  gallery.addEventListener('click', event => {
    const opener = event.target.closest('.pd-open');
    if (!opener) return;
    const shown = [...gallery.querySelectorAll('figure:not([hidden])')];
    openLightbox(Math.max(0, shown.indexOf(opener.closest('figure'))));
  });
  if (lightbox) {
    lightbox.addEventListener('close', () => {
      clearTimeout(settleTimer);
      landing = null;
    });
    lightbox.addEventListener('click', event => {
      const step = event.target.closest('[data-step]');
      if (step) goTo(current + Number(step.dataset.step));
      const thumb = event.target.closest('.pd-lb-thumb');
      if (thumb) goTo(Number(thumb.dataset.index));
    });
    lightbox.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    // a swipe or trackpad scroll moves the counter as it goes; during a goTo() each scroll event restarts the wait
    let frame = 0;
    track.addEventListener('scroll', () => {
      if (!lightbox.open) return;
      if (landing !== null) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, SETTLE_MS);
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setCurrent(slideAt()));
    });
    track.addEventListener('scrollend', settle);
    // a resize leaves the track between slides: put the current one back
    new ResizeObserver(() => { if (lightbox.open) track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' }); }).observe(track);
  }

  // userChange: only a shopper's choice writes ?variant= into the URL, keeping every other parameter (utm, fbclid).
  function drawOptions(userChange = false) {
    if (colorIndex !== null) get('pd-color').textContent = color;
    swatchButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.color === color)));
    sizeButtons.forEach(button => {
      const variant = sizeVariant(button.dataset.size);
      button.disabled = !variant;
      button.toggleAttribute('data-sold-out', Boolean(variant && !variant.available));
      button.setAttribute('aria-label', `${button.dataset.size}${variant && !variant.available ? ', sold out' : ''}`);
      button.setAttribute('aria-pressed', String(button.dataset.size === size));
    });
    const variant = selected();
    if (variant) {
      get('pd-price').textContent = variant.price;
      if (variantInput.value !== String(variant.id)) {
        variantInput.value = variant.id;
        // Shopify's accelerated checkout follows the form's id input by its change event
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (userChange) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
    }
    const soldOut = Boolean(variant && !variant.available);
    const express = root.querySelector('.pd-express');
    if (express) express.hidden = !variant || soldOut;
    // not disabled while adding, so keyboard focus stays on the button; the busy flag blocks repeats
    add.disabled = !variant || soldOut;
    add.textContent = !variant ? data.selectLabel : soldOut ? 'Sold out' : data.addLabel;
    // a sold-out pre-order hides its ship line, also before a size is chosen when the color has none left (as the
    // server renders it); a hidden line must not describe the button. The final-sale line never hides.
    if (preorder) preorder.hidden = soldOut || !variants.some(v => colorMatches(v) && v.available);
    if (preorder || finalSale) {
      const described = [finalSale, preorder].filter(line => line && !line.hidden).map(line => line.id).join(' ');
      if (described) add.setAttribute('aria-describedby', described);
      else add.removeAttribute('aria-describedby');
    }
    if (notify) {
      notify.hidden = !soldOut;
      if (variant) {
        notify.dataset.variantId = variant.id;
        notify.dataset.variantTitle = variant.title;
      }
    }
  }

  // "Details matter" and "Stallys in the Wild" (sections/stally-rd-product-details.liquid, -wild.liquid):
  // data-color="" marks the set for every color.
  function drawDetails() {
    document.querySelectorAll('[data-details]').forEach(section => {
      const sets = [...section.querySelectorAll('.pd-detail-set')];
      const match = sets.find(set => set.dataset.color === color) || sets.find(set => !set.dataset.color);
      sets.forEach(set => { set.hidden = set !== match; });
      section.hidden = !match;
    });
  }

  get('pd-swatches')?.addEventListener('click', event => {
    const button = event.target.closest('button.swatch');
    if (!button) return;
    color = button.dataset.color;
    drawGallery();
    drawDetails();
    drawOptions(true);
  });
  get('pd-sizes')?.addEventListener('click', event => {
    const button = event.target.closest('.size-choice');
    if (!button || button.disabled || button.hasAttribute('data-one-size')) return;
    size = button.dataset.size;
    status.textContent = '';
    drawOptions(true);
  });
  // The size guide button opens this page's Size Guide accordion and brings it into view, with focus on its summary;
  // without a chart (hats) the button links to the size guide page instead, and none of this runs.
  const guideButton = root.querySelector('.pd-size-guide[href="#pd-size-guide"]');
  // opens and scrolls to the accordion; returns its summary for the caller to focus
  const openGuide = event => {
    const guide = get('pd-size-guide');
    if (!guide) return null;
    event.preventDefault();
    guide.open = true;
    guide.scrollIntoView({ behavior: motion(), block: 'start' });
    return guide.querySelector('summary');
  };
  guideButton?.addEventListener('click', event => openGuide(event)?.focus({ preventScroll: true }));
  // Enter opens on keydown but moves focus on keyup: focused on keydown, a held Enter's repeats would reach the summary
  // and toggle it
  let enterPending = false;
  guideButton?.addEventListener('keydown', event => { if (event.key === 'Enter') enterPending = Boolean(openGuide(event)); });
  guideButton?.addEventListener('keyup', event => {
    if (event.key !== 'Enter' || !enterPending) return;
    enterPending = false;
    get('pd-size-guide').querySelector('summary').focus({ preventScroll: true });
  });
  // focus left before the keyup (Tab with Enter held): a later stray keyup must not pull focus to the summary
  guideButton?.addEventListener('blur', () => { enterPending = false; });

  // Gift cards: the recipient fields post only while "send as a gift" is ticked, as Shopify expects.
  const recipientToggle = get('pd-recipient-toggle');
  // the no-JS fallback field would post a second value; with JS the checkbox decides (as Concept's recipient-form.js)
  const recipientControl = get('pd-recipient-control');
  if (recipientControl) recipientControl.disabled = true;
  recipientToggle?.addEventListener('change', () => {
    const fields = get('pd-recipient-fields');
    fields.hidden = !recipientToggle.checked;
    fields.querySelectorAll('input, textarea').forEach(field => { field.disabled = !recipientToggle.checked; });
    get('pd-recipient-offset').value = new Date().getTimezoneOffset();
  });

  form.addEventListener('submit', async event => {
    event.preventDefault();
    const variant = selected();
    if (busy || !variant?.available) return;
    busy = true;
    add.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(cartUrl('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || `Add to bag failed: ${response.status}`);
      const cart = await (await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })).json();
      document.dispatchEvent(new CustomEvent('stally:cart', { detail: cart }));
      // design copy on the copy sheet: reword only there
      status.innerHTML = `Added to your rotation. <a href="${cartUrl('cart')}">View bag</a>`;
    } catch (error) {
      status.textContent = error.message;
    } finally {
      busy = false;
      add.removeAttribute('aria-busy');
      drawOptions();
    }
  });

  drawGallery();
  drawOptions();
}

// "You may also like": Shopify's related products, rendered by the same section.
function initRecommendations(root = document) {
  const more = root.querySelector('[data-recommendations]');
  if (!more) return;
  fetch(more.dataset.recommendations)
    .then(response => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
    .then(html => {
      const cards = new DOMParser().parseFromString(html, 'text/html').getElementById('pd-more');
      if (cards?.children.length) more.querySelector('#pd-more').replaceChildren(...cards.children);
      else more.hidden = true;
    })
    .catch(error => { more.hidden = true; console.warn('You may also like: recommendations unavailable.', error); });
}

// A Details matter or Stallys in the Wild photo opens the gallery with every photo of its set, past the fifth tile
// too. One document handler, added once, so a section the theme editor reloads is still served.
document.addEventListener('click', event => {
  const opener = event.target.closest?.('.pd-detail-open');
  if (!opener || !openGallery) return;
  const photos = [...opener.closest('.pd-detail-set').querySelectorAll('figure img')];
  openGallery(Math.max(0, photos.indexOf(opener.querySelector('img'))), photos);
});

initProduct();
initRecommendations();
// the theme editor re-renders a section without re-running scripts
document.addEventListener('shopify:section:load', event => {
  initProduct(event.target);
  initRecommendations(event.target);
});
